import { create } from 'zustand'

/**
 * Navigation state (mode, tab, product, persona, detail, history) has moved
 * to the URL via TanStack Router. Use useAppNav() for all navigation reads
 * and writes.
 *
 * This store now owns only non-URL UI state that is transient by design
 * (resets on refresh is acceptable for all of these).
 */
export const useAppViewStore = create((set) => ({
  // MapLibre GL JS instance — MIRROR of MapView's local mapInstance state.
  // Truth-of-record lives in MapView (line ~141 useState + line ~632
  // setMapInstance after style.load); this slot is written immediately
  // after that local set so panel-mounted consumers can read without
  // props plumbing. Should be non-null whenever MapView is mounted on
  // the current route. Resets to null only on hard refresh or MapView
  // unmount.
  //
  // When to use this slot vs. when to receive map as a prop:
  //   - PANEL-MOUNTED components (Padrekko QC drawer, AOI panel tools,
  //     any feature whose lifecycle is bound to a side-panel tab): read
  //     from here. Prop-drilling through 4 levels of panel components
  //     is more fragile than a global read.
  //   - MAPVIEW-CHILDREN components (PadLayer, PadAccessRoadLayer,
  //     PadRoadDrawer): receive map as a prop from MapView's local
  //     state. They share MapView's mount lifecycle directly; props
  //     are honest about that coupling.
  mapInstance: null,
  setMapInstance: (map) => set({ mapInstance: map }),

  // MlDraw control instance. Same MIRROR pattern as mapInstance, but
  // truth lives in MapView's local drawInstance state (set in the same
  // style.load handler). MapPage's handleDrawReady writes to this slot
  // for the same panel-consumer reason.
  drawInstance: null,
  setDrawInstance: (draw) => set({ drawInstance: draw }),

  // Left panel open/close state
  sidebarOpen: true,
  setSidebarOpen: (open) => set({ sidebarOpen: open }),
  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),

  // Supplemental drawer slot (used by detail panels, context sheets, etc.)
  drawerContent: null,
  openDrawer: (content) => set({ drawerContent: content }),
  closeDrawer: () => set({ drawerContent: null }),
}))